import AppEnvironment from '../../../components/enums/appEnvironment.enum';
import { isOfflineStatus } from '../../offline/domain/offlineOrder/offlineOrder.types';
import { orderSummaryAllActions } from './orderSummaryActions';
import { ActionsToRemoveType, ActionsType, GetActionsParams } from './types/actions.types';
import { ActionEnum, OrderAction } from './types/orderActions.types';
import {
  FeatureFlagToVerify,
  getActionToRemoveBasedOnArchivedOrder,
  getActionsToRemoveBasedOnGatewayPayment,
  getActionsToRemoveBasedOnIntegrationStatus,
  getActionsToRemoveBySystemParams,
} from './utils/actions.utils';

export class Filter {
  private actions: ActionsType[] = orderSummaryAllActions;

  private removeActions(actionsToRemove: ActionsToRemoveType): void {
    this.actions = this.actions.filter((action) => !actionsToRemove.includes(action.value));
  }

  filterActions(params: GetActionsParams, onlyMassActions: boolean): void {
    const {
      isSystemParamsResetIntegration,
      isEnableShareSuggestedOrder,
      status,
      archivedOrder,
      role,
      hasIntegrations,
      synchronized,
      triggerIntegrationConfig,
      hasSplit,
      loggedin = true,
      splitted,
      removeActions = [],
      hasOrderIntegration,
      userHasNotInternet,
      isEnableAcceptSuggestedOrder,
      userAbility,
      isArchivedOrderFilterActive = false,
      appEnvironment = AppEnvironment.BRAND,
    } = params;

    const includesRoleOrAbility = (action: ActionsType): boolean => {
      const hasRole = action.conditions.statusRole[status]?.includes(role);

      const resourceTypes = action.conditions.statusAbility?.[status];
      const hasAbility = resourceTypes?.some((resourceType) =>
        userAbility?.can('update', resourceType),
      );

      return hasRole || hasAbility || false;
    };

    const isActionType = (action: ActionsType): boolean =>
      onlyMassActions ? action.conditions.massAction : true;

    const isAllowed = (action: ActionsType, params: GetActionsParams): boolean => {
      if (!action?.conditions?.isAllowed) {
        return true;
      }

      return action.conditions.isAllowed(params);
    };

    const isAllowedByAppEnvironment = (action: ActionsType): boolean =>
      action.conditions.appEnvironment.includes(appEnvironment);

    this.actions = this.actions.filter(
      (action) =>
        isActionType(action) &&
        includesRoleOrAbility(action) &&
        isAllowed(action, params) &&
        isAllowedByAppEnvironment(action),
    );

    const featureFlagsToVerify: FeatureFlagToVerify[] = [
      { valueSystemParam: isSystemParamsResetIntegration, actionEnum: ActionEnum.SYNC },
      { valueSystemParam: isEnableShareSuggestedOrder, actionEnum: ActionEnum.SHARE_ORDER },
      { valueSystemParam: isEnableAcceptSuggestedOrder, actionEnum: ActionEnum.ACCEPT_SUGGESTION },
    ];

    const actionsToRemove: ActionEnum[] = [
      ...removeActions,
      ...getActionsToRemoveBySystemParams(featureFlagsToVerify),
      ...getActionToRemoveBasedOnArchivedOrder(archivedOrder),
      ...(isOfflineStatus(status)
        ? []
        : getActionsToRemoveBasedOnIntegrationStatus({
            hasIntegrations,
            synchronized,
            triggerIntegrationConfig,
            status,
          })),
      ...getActionsToRemoveBasedOnGatewayPayment({
        params,
        role,
        ability: userAbility,
        integrationTrigger: triggerIntegrationConfig,
      }),
    ];

    if (!hasSplit || splitted) {
      actionsToRemove.push(ActionEnum.SPLIT_ORDER);
    }

    if (!loggedin) {
      actionsToRemove.push(ActionEnum.COPY_AS_DRAFT);
    }

    if (!hasOrderIntegration) {
      actionsToRemove.push(ActionEnum.SYNC, ActionEnum.CHECK_ORDER_INTEGRATION);
    }

    if (userHasNotInternet) {
      actionsToRemove.push(ActionEnum.SYNC_OFFLINE_ORDER);
    }

    if (!onlyMassActions || userAbility?.cannot('create', 'export-order-pdf')) {
      actionsToRemove.push(ActionEnum.EXPORT_MULTIPLE_ORDERS);
    }

    if (isArchivedOrderFilterActive || archivedOrder) {
      this.actions = this.actions.filter((action) => action.value === ActionEnum.UNARCHIVE_ORDER);
    } else {
      this.removeActions(actionsToRemove);
    }
  }

  getActions(): OrderAction[] {
    return this.actions.map((action) => ({
      icon: action.icon,
      label: action.label,
      value: action.value,
      testId: action.testId,
    }));
  }
}
